import {
  Box, Typography, LinearProgress, Card, CardContent, Grid, Chip, Stack,
  Button, Snackbar, Alert, Divider, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Tooltip, TextField,
  FormControl, InputLabel, Select, MenuItem, IconButton,
  InputAdornment,
} from "@mui/material";
import {
  CheckCircle, Cancel, DoneAll, WarningAmber, CompareArrows,
  Search, FilterList, Edit, Save,
} from "@mui/icons-material";
import { useState, useCallback, useMemo } from "react";
import { useMutation, useQuery } from "convex/react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { useCycleFilter } from "../cycleFilterContext";
import { QbPostedBadge } from "../components/qb/QbPostedBadge";
import { HowToFindModal } from "../components/qb/HowToFindModal";
import type { QbAwareTransactionRow } from "../types/qb-aware";
import { DocumentCompareDrawer } from "../components/viewer/DocumentCompareDrawer";
import { AiDisclaimer } from "../components/AiDisclaimer";
import {
  ORIGIN_BUCKETS,
  ORIGIN_BUCKET_ORDER,
  originBucketFor,
  type OriginBucketKey,
} from "../utils/originBuckets";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function confidenceColor(score: number): "success" | "primary" | "warning" | "error" {
  if (score >= 95) return "success";
  if (score >= 80) return "primary";
  if (score >= 50) return "warning";
  return "error";
}

/** Color for score bar: green >= 80, yellow 50-79, red < 50 */
function scoreBarColor(score: number): string {
  if (score >= 80) return "#00b894";
  if (score >= 50) return "#fdcb6e";
  return "#ff7675";
}

/** Horizontal score bar with label */
function ScoreBar({ label, score, weight }: { label: string; score: number; weight: string }) {
  const pct = Math.min(score, 100);
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
      <Typography variant="caption" sx={{ minWidth: 80, color: "text.secondary", fontSize: "0.7rem" }}>
        {label} ({weight})
      </Typography>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: "var(--c-overlay-6)", overflow: "hidden" }}>
        <Box sx={{
          width: `${pct}%`,
          height: "100%",
          borderRadius: 4,
          bgcolor: scoreBarColor(pct),
          transition: "width 0.3s ease",
        }} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 32, textAlign: "right", fontWeight: 600, fontSize: "0.7rem" }}>
        {pct.toFixed(0)}%
      </Typography>
    </Stack>
  );
}

// ─── Side-by-side compare drawer ───
//
// Implementation extracted to components/viewer/DocumentCompareDrawer.tsx so
// Exceptions, AuditTrail, and PipelineErrors can reuse the same UX. This file
// keeps a thin wrapper that resolves the bank statement file via
// `transactions.getWithSourceFile` and forwards both panels to the shared
// drawer.

function CompareDrawer({
  open,
  onClose,
  match,
}: {
  open: boolean;
  onClose: () => void;
  match: any;
}) {
  const txnWithSource = useQuery(
    api.transactions.getWithSourceFile,
    match?.transaction?._id && open ? { id: match.transaction._id } : "skip",
  );

  const bankStatementStorageId = txnWithSource?.sourceFile?.storageId ?? null;
  const invoiceStorageId = match?.invoice?.fileStorageId ?? null;

  const subtitle = match
    ? `${match.transaction?.vendorName ?? match.invoice?.vendorName ?? ""} — ${formatCurrency(Math.abs(match.transaction?.amount ?? 0))} vs ${formatCurrency(match.invoice?.amount ?? 0)}`
    : undefined;

  return (
    <DocumentCompareDrawer
      open={open}
      onClose={onClose}
      title="Document Comparison"
      subtitle={subtitle}
      left={{ storageId: bankStatementStorageId, label: "Bank Statement" }}
      right={{ storageId: invoiceStorageId, label: "Invoice" }}
    />
  );
}

// ─── Filter types ───

type AmountFilter = "all" | "exact" | "small_diff" | "large_diff";
type ScoreFilter = "all" | "high" | "medium" | "low";

// ─── Main component ───

// ─── Edit state types ───

interface EditFields {
  // Transaction fields
  txnAmount?: string;
  txnDate?: string;
  txnDescription?: string;
  txnVendor?: string;
  // Invoice fields
  invAmount?: string;
  invDate?: string;
  invVendor?: string;
  invNumber?: string;
}

export function MatchReview() {
  const { cycleArg } = useCycleFilter();
  const pendingMatches = useStableQuery(api.matches.listPendingReviewEnriched, { ...cycleArg, limit: 200 }, []);
  const updateStatus = useMutation(api.matches.updateStatus);
  const batchUpdate = useMutation(api.matches.batchUpdateStatus);
  const updateTransaction = useMutation(api.transactions.update);
  const updateInvoice = useMutation(api.invoices.update);
  const insertAuditLog = useMutation(api.auditLog.insert);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; matchId: string | null }>({
    open: false,
    matchId: null,
  });
  const [compareMatch, setCompareMatch] = useState<any | null>(null);
  // W4 — "How to find in QB" modal target (a transaction with .qb populated)
  const [howToTarget, setHowToTarget] = useState<QbAwareTransactionRow | null>(null);

  // Edit state: which match is being edited, field values, and save-reason dialog
  const [editingMatchId, setEditingMatchId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<EditFields>({});
  const [saveReasonDialog, setSaveReasonDialog] = useState<{ open: boolean; reason: string }>({ open: false, reason: "" });

  // Filters
  const [searchText, setSearchText] = useState("");
  const [amountFilter, setAmountFilter] = useState<AmountFilter>("all");
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter>("all");
  const [originFilter, setOriginFilter] = useState<OriginBucketKey>("all");

  const showSnackbar = useCallback((message: string, severity: "success" | "error") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Per-origin counts so the filter dropdown shows live numbers
  // alongside each option. `all` is the total of all hydrated matches.
  const originCounts = useMemo(() => {
    const counts: Record<OriginBucketKey, number> = {
      all: 0,
      credit_card: 0,
      amex: 0,
      operating: 0,
      payroll: 0,
      wire: 0,
      merchant: 0,
      checking: 0,
      other: 0,
    };
    if (!pendingMatches) return counts;
    for (const m of pendingMatches as any[]) {
      counts.all++;
      const key = originBucketFor(
        m.transaction?.sourceFileName,
        m.transaction?.sourceFileCategory,
      );
      counts[key]++;
    }
    return counts;
  }, [pendingMatches]);

  // Apply filters
  const filteredMatches = useMemo(() => {
    if (!pendingMatches) return [];
    return pendingMatches.filter((match: any) => {
      // Text search across vendor, description, invoice number
      if (searchText.trim()) {
        const q = searchText.toLowerCase();
        const txn = match.transaction ?? {};
        const inv = match.invoice ?? {};
        const haystack = [
          txn.vendorName, txn.description, txn.reference,
          inv.vendorName, inv.invoiceNumber, inv.source,
          String(Math.abs(txn.amount ?? 0)), String(inv.amount ?? 0),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      // Origin filter (v3.0.70) — bucket the transaction by the
      // source document type (Credit Card vs each Bank account).
      if (originFilter !== "all") {
        const bucket = originBucketFor(
          match.transaction?.sourceFileName,
          match.transaction?.sourceFileCategory,
        );
        if (bucket !== originFilter) return false;
      }

      // Amount divergence filter
      if (amountFilter !== "all") {
        const txnAbs = Math.abs(match.transaction?.amount ?? 0);
        const invAmt = match.invoice?.amount ?? 0;
        const diff = Math.abs(txnAbs - invAmt);
        const pctDiff = Math.max(txnAbs, invAmt) > 0 ? diff / Math.max(txnAbs, invAmt) : 0;

        if (amountFilter === "exact" && diff >= 0.01) return false;
        if (amountFilter === "small_diff" && (diff < 0.01 || pctDiff > 0.05)) return false;
        if (amountFilter === "large_diff" && pctDiff <= 0.05) return false;
      }

      // Score filter
      if (scoreFilter !== "all") {
        const score = match.confidenceScore ?? 0;
        if (scoreFilter === "high" && score < 90) return false;
        if (scoreFilter === "medium" && (score < 70 || score >= 90)) return false;
        if (scoreFilter === "low" && score >= 70) return false;
      }

      return true;
    });
  }, [pendingMatches, searchText, amountFilter, scoreFilter, originFilter]);

  if (pendingMatches === undefined) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Match Review
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review and approve pending transaction-invoice matches
        </Typography>
        <LinearProgress sx={{ borderRadius: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading data...
        </Typography>
      </Box>
    );
  }

  // ── Edit handlers ──

  const startEditing = (match: any) => {
    setEditingMatchId(match._id);
    setEditFields({
      txnAmount: match.transaction ? String(match.transaction.amount) : "",
      txnDate: match.transaction?.date ?? "",
      txnDescription: match.transaction?.description ?? "",
      txnVendor: match.transaction?.vendorName ?? "",
      invAmount: match.invoice ? String(match.invoice.amount) : "",
      invDate: match.invoice?.date ?? "",
      invVendor: match.invoice?.vendorName ?? "",
      invNumber: match.invoice?.invoiceNumber ?? "",
    });
  };

  const cancelEditing = () => {
    setEditingMatchId(null);
    setEditFields({});
  };

  const hasEditChanges = (match: any): boolean => {
    if (!match) return false;
    const txn = match.transaction ?? {};
    const inv = match.invoice ?? {};
    return (
      editFields.txnAmount !== String(txn.amount ?? "") ||
      editFields.txnDate !== (txn.date ?? "") ||
      editFields.txnDescription !== (txn.description ?? "") ||
      editFields.txnVendor !== (txn.vendorName ?? "") ||
      editFields.invAmount !== String(inv.amount ?? "") ||
      editFields.invDate !== (inv.date ?? "") ||
      editFields.invVendor !== (inv.vendorName ?? "") ||
      editFields.invNumber !== (inv.invoiceNumber ?? "")
    );
  };

  const handleSaveEdits = async () => {
    const reason = saveReasonDialog.reason.trim();
    if (!reason) return;

    const match = pendingMatches?.find((m: any) => m._id === editingMatchId);
    if (!match) return;

    const txn = match.transaction ?? {};
    const inv = match.invoice ?? {};
    const changes: Record<string, { from: unknown; to: unknown }> = {};

    try {
      // Build transaction changes
      const txnPatch: Record<string, unknown> = {};
      const newTxnAmount = parseFloat(editFields.txnAmount ?? "");
      if (!isNaN(newTxnAmount) && newTxnAmount !== txn.amount) {
        txnPatch.amount = newTxnAmount;
        changes["transaction.amount"] = { from: txn.amount, to: newTxnAmount };
      }
      if (editFields.txnDate && editFields.txnDate !== txn.date) {
        txnPatch.date = editFields.txnDate;
        changes["transaction.date"] = { from: txn.date, to: editFields.txnDate };
      }
      if (editFields.txnDescription && editFields.txnDescription !== txn.description) {
        txnPatch.description = editFields.txnDescription;
        changes["transaction.description"] = { from: txn.description, to: editFields.txnDescription };
      }
      if (editFields.txnVendor !== undefined && editFields.txnVendor !== (txn.vendorName ?? "")) {
        txnPatch.vendorName = editFields.txnVendor || undefined;
        changes["transaction.vendorName"] = { from: txn.vendorName, to: editFields.txnVendor };
      }

      // Build invoice changes
      const invPatch: Record<string, unknown> = {};
      const newInvAmount = parseFloat(editFields.invAmount ?? "");
      if (!isNaN(newInvAmount) && newInvAmount !== inv.amount) {
        invPatch.amount = newInvAmount;
        changes["invoice.amount"] = { from: inv.amount, to: newInvAmount };
      }
      if (editFields.invDate && editFields.invDate !== inv.date) {
        invPatch.date = editFields.invDate;
        changes["invoice.date"] = { from: inv.date, to: editFields.invDate };
      }
      if (editFields.invVendor !== undefined && editFields.invVendor !== (inv.vendorName ?? "")) {
        invPatch.vendorName = editFields.invVendor;
        changes["invoice.vendorName"] = { from: inv.vendorName, to: editFields.invVendor };
      }
      if (editFields.invNumber !== undefined && editFields.invNumber !== (inv.invoiceNumber ?? "")) {
        invPatch.invoiceNumber = editFields.invNumber || undefined;
        changes["invoice.invoiceNumber"] = { from: inv.invoiceNumber, to: editFields.invNumber };
      }

      // Apply transaction update
      if (Object.keys(txnPatch).length > 0 && txn._id) {
        await updateTransaction({ id: txn._id, ...txnPatch } as any);
      }

      // Apply invoice update
      if (Object.keys(invPatch).length > 0 && inv._id) {
        await updateInvoice({ id: inv._id, ...invPatch } as any);
      }

      // Audit log
      await insertAuditLog({
        agentId: "admin",
        action: "manual_data_edit",
        entityType: "match",
        entityId: match._id,
        details: JSON.stringify({
          reason,
          changes,
          matchId: match._id,
          transactionId: txn._id,
          invoiceId: inv._id,
          editedAt: new Date().toISOString(),
        }),
        severity: "warning",
        reconciliationCycleId: match.reconciliationCycleId,
      });

      setEditingMatchId(null);
      setEditFields({});
      setSaveReasonDialog({ open: false, reason: "" });
      showSnackbar("Changes saved with audit trail", "success");
    } catch {
      showSnackbar("Failed to save changes", "error");
    }
  };

  // ── Match action handlers ──

  const handleApprove = async (matchId: any) => {
    try {
      await updateStatus({ id: matchId, status: "human_confirmed", reviewedBy: "admin" });
      setSelected((prev) => { const next = new Set(prev); next.delete(matchId); return next; });
      showSnackbar("Match approved", "success");
    } catch {
      showSnackbar("Failed to approve match", "error");
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectDialog.matchId) return;
    try {
      await updateStatus({ id: rejectDialog.matchId as any, status: "human_rejected", reviewedBy: "admin" });
      setSelected((prev) => { const next = new Set(prev); next.delete(rejectDialog.matchId!); return next; });
      showSnackbar("Match rejected", "success");
    } catch {
      showSnackbar("Failed to reject match", "error");
    }
    setRejectDialog({ open: false, matchId: null });
  };

  const handleBatchApprove = async () => {
    if (selected.size === 0) return;
    try {
      await batchUpdate({
        ids: Array.from(selected) as any[],
        status: "human_confirmed",
        reviewedBy: "admin",
      });
      setSelected(new Set());
      showSnackbar(`${selected.size} matches approved`, "success");
    } catch {
      showSnackbar("Failed to batch approve", "error");
    }
  };

  const toggleSelect = (matchId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId);
      else next.add(matchId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredMatches.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredMatches.map((m: any) => m._id)));
    }
  };

  const activeFilters =
    (searchText.trim() ? 1 : 0) +
    (amountFilter !== "all" ? 1 : 0) +
    (scoreFilter !== "all" ? 1 : 0) +
    (originFilter !== "all" ? 1 : 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }} data-tour="page-title-matches">
        Match Review
      </Typography>
      <AiDisclaimer context="These matches are proposed by AI and can be wrong" />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Review and approve pending transaction-invoice matches
          {" \u2014 "}
          <Box component="span" sx={{ color: "var(--c-link-accent)", fontWeight: 600 }}>
            {pendingMatches.length} pending
          </Box>
          {activeFilters > 0 && (
            <Box component="span" sx={{ color: "var(--c-metric-warning)", fontWeight: 600, ml: 1 }}>
              ({filteredMatches.length} shown)
            </Box>
          )}
        </Typography>

        {pendingMatches.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              size="small"
              checked={selected.size === filteredMatches.length && filteredMatches.length > 0}
              indeterminate={selected.size > 0 && selected.size < filteredMatches.length}
              onChange={toggleSelectAll}
            />
            <Typography variant="caption" color="text.secondary">Select all</Typography>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<DoneAll />}
              disabled={selected.size === 0}
              onClick={handleBatchApprove}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Approve {selected.size > 0 ? `(${selected.size})` : ""}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* ── Filters row ── */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center" data-tour="match-filters">
        <TextField
          size="small"
          placeholder="Search vendor, description, amount..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />,
          }}
          sx={{ flex: 1, maxWidth: 400 }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Amount Match</InputLabel>
          <Select
            value={amountFilter}
            label="Amount Match"
            onChange={(e) => setAmountFilter(e.target.value as AmountFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="exact">Exact Match</MenuItem>
            <MenuItem value="small_diff">Small Diff (&le;5%)</MenuItem>
            <MenuItem value="large_diff">Large Diff (&gt;5%)</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Confidence</InputLabel>
          <Select
            value={scoreFilter}
            label="Confidence"
            onChange={(e) => setScoreFilter(e.target.value as ScoreFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="high">High (&ge;90)</MenuItem>
            <MenuItem value="medium">Medium (70-89)</MenuItem>
            <MenuItem value="low">Low (&lt;70)</MenuItem>
          </Select>
        </FormControl>

        {/* v3.0.70 — Origin filter scopes review to a specific source
            document type (Credit Card statement vs each Bank account).
            Each option carries an icon, accent color, and live count
            for the active cycle. */}
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Origin</InputLabel>
          <Select
            value={originFilter}
            label="Origin"
            onChange={(e) => setOriginFilter(e.target.value as OriginBucketKey)}
            renderValue={(v) => {
              const def = ORIGIN_BUCKETS[v as OriginBucketKey];
              const Icon = def.Icon;
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Icon fontSize="small" />
                  <span>{def.label}</span>
                  <Chip
                    size="small"
                    label={originCounts[v as OriginBucketKey]}
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      backgroundColor: "var(--c-overlay-4)",
                      color: "var(--c-text-secondary)",
                      ml: 0.5,
                    }}
                  />
                </Stack>
              );
            }}
          >
            {ORIGIN_BUCKET_ORDER.map((key) => {
              const def = ORIGIN_BUCKETS[key];
              const Icon = def.Icon;
              const count = originCounts[key];
              return (
                <MenuItem key={key} value={key} disabled={key !== "all" && count === 0}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                    <Box sx={{ display: "flex", color: def.color }}>
                      <Icon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1 }}>{def.label}</Box>
                    <Chip
                      size="small"
                      label={count}
                      sx={{
                        height: 18,
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        backgroundColor:
                          originFilter === key ? def.color : "var(--c-overlay-4)",
                        color:
                          originFilter === key ? "#fff" : "var(--c-text-secondary)",
                      }}
                    />
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {activeFilters > 0 && (
          <Chip
            icon={<FilterList sx={{ fontSize: 16 }} />}
            label={`${activeFilters} filter${activeFilters > 1 ? "s" : ""}`}
            size="small"
            onDelete={() => {
              setSearchText("");
              setAmountFilter("all");
              setScoreFilter("all");
              setOriginFilter("all");
            }}
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>

      {filteredMatches.length === 0 ? (
        <Card sx={{ borderLeft: "3px solid", borderImage: "linear-gradient(180deg, #6c5ce7, #00cec9) 1" }}>
          <CardContent>
            <Typography color="text.secondary">
              {pendingMatches.length === 0
                ? "No pending matches to review. All matches have been processed."
                : "No matches found with the current filters."}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={3}>
          {filteredMatches.map((match: any) => {
            const txnAbs = Math.abs(match.transaction?.amount ?? 0);
            const invAmt = match.invoice?.amount ?? 0;
            const amountDiff = Math.abs(txnAbs - invAmt);
            const hasAmountDiff = amountDiff >= 0.01;
            const isEditing = editingMatchId === match._id;

            const ef = (field: keyof EditFields) => isEditing ? editFields[field] ?? "" : "";
            const setEf = (field: keyof EditFields, value: string) => setEditFields((prev) => ({ ...prev, [field]: value }));

            return (
              <Card
                key={match._id}
                {...(match === filteredMatches[0] ? { "data-tour": "match-card" } : {})}
                sx={{
                  borderLeft: "3px solid",
                  borderImage: isEditing
                    ? "linear-gradient(180deg, #fdcb6e, #e17055) 1"
                    : "linear-gradient(180deg, #6c5ce7, #00cec9) 1",
                  transition: "box-shadow 0.2s ease",
                  outline: selected.has(match._id) ? "2px solid rgba(108,92,231,0.5)" : "none",
                  "&:hover": {
                    boxShadow: "0 4px 24px rgba(108, 92, 231, 0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Checkbox row */}
                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Checkbox
                      size="small"
                      checked={selected.has(match._id)}
                      onChange={() => toggleSelect(match._id)}
                      disabled={isEditing}
                    />
                    <Chip
                      label={`${match.confidenceScore}% Overall`}
                      color={confidenceColor(match.confidenceScore)}
                      sx={{ fontWeight: 700, ml: 1 }}
                    />
                    {hasAmountDiff && (
                      <Chip
                        label={`${amountDiff < 1 ? "\u00B1" : "\u0394"}${formatCurrency(amountDiff)}`}
                        size="small"
                        color={amountDiff < 1 ? "default" : amountDiff < 10 ? "warning" : "error"}
                        variant="outlined"
                        sx={{ ml: 1, fontWeight: 600 }}
                      />
                    )}
                    {isEditing && (
                      <Chip label="Editing" size="small" color="warning" sx={{ ml: 1, fontWeight: 600 }} />
                    )}
                  </Stack>

                  <Grid container spacing={3}>
                    {/* Transaction (left) */}
                    <Grid item xs={12} md={5}>
                      <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: "rgba(108, 92, 231, 0.06)", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ color: "var(--c-link-accent)", fontWeight: 700, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", mb: 1.5 }}>
                          Transaction
                        </Typography>
                        {match.transaction ? (
                          isEditing ? (
                            <Stack spacing={1.5}>
                              <TextField size="small" label="Date" type="date" fullWidth
                                value={ef("txnDate")}
                                onChange={(e) => setEf("txnDate", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField size="small" label="Description" fullWidth
                                value={ef("txnDescription")}
                                onChange={(e) => setEf("txnDescription", e.target.value)}
                              />
                              <TextField size="small" label="Vendor" fullWidth
                                value={ef("txnVendor")}
                                onChange={(e) => setEf("txnVendor", e.target.value)}
                              />
                              <TextField size="small" label="Amount" fullWidth type="number"
                                value={ef("txnAmount")}
                                onChange={(e) => setEf("txnAmount", e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              />
                            </Stack>
                          ) : (
                            <Stack spacing={0.75}>
                              <Typography variant="body2">
                                <Box component="span" sx={{ color: "text.secondary" }}>Date:</Box>{" "}
                                {formatDate(match.transaction.date)}
                              </Typography>
                              <Typography variant="body2">
                                <Box component="span" sx={{ color: "text.secondary" }}>Description:</Box>{" "}
                                {match.transaction.description}
                              </Typography>
                              <Typography variant="body2">
                                <Box component="span" sx={{ color: "text.secondary" }}>Vendor:</Box>{" "}
                                {match.transaction.vendorName ?? "-"}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.95rem", mt: 0.5 }}>
                                <Box component="span" sx={{ color: "text.secondary", fontWeight: 400, fontSize: "0.875rem" }}>Amount:</Box>{" "}
                                <Box component="span" sx={hasAmountDiff ? { color: "var(--c-metric-warning)", border: "1px solid rgba(253,203,110,0.3)", borderRadius: 0.5, px: 0.5 } : {}}>
                                  {formatCurrency(match.transaction.amount)}
                                </Box>
                              </Typography>
                              {/* W4 — Where in QB badge + how-to-find affordance */}
                              <Box sx={{ mt: 1 }}>
                                <QbPostedBadge
                                  qbTransactionId={(match.transaction as any).qbTransactionId}
                                  qb={(match.transaction as any).qb}
                                  txDate={match.transaction.date}
                                  drift={(match.transaction as any).qbDrift}
                                />
                              </Box>
                              {(match.transaction as any).qbTransactionId && (match.transaction as any).qb && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: "var(--c-bg-elevated)", borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ display: "block" }}>
                                    Posted to: {(match.transaction as any).qb.accountName ?? "-"} · Ref: {(match.transaction as any).qb.refNumber ?? "-"}
                                  </Typography>
                                  <Button
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                    onClick={() => setHowToTarget(match.transaction as any)}
                                  >
                                    How to find in QB
                                  </Button>
                                </Box>
                              )}
                            </Stack>
                          )
                        ) : (
                          <Typography variant="body2" color="error">Transaction not found</Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Confidence breakdown (center) */}
                    <Grid item xs={12} md={2}>
                      <Box sx={{ py: 2, px: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: "var(--c-text-secondary)", fontWeight: 700, textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: "0.1em", mb: 1.5, textAlign: "center" }}>
                          Score Breakdown
                        </Typography>
                        {match.matchCriteria && (
                          <Box>
                            <ScoreBar label="Amount" score={match.matchCriteria.amountScore} weight="35%" />
                            <ScoreBar label="Vendor" score={match.matchCriteria.vendorScore} weight="30%" />
                            <ScoreBar label="Date" score={match.matchCriteria.dateScore} weight="20%" />
                            <ScoreBar label="Description" score={match.matchCriteria.descriptionScore} weight="15%" />
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Invoice (right) */}
                    <Grid item xs={12} md={5}>
                      <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: "rgba(0, 206, 201, 0.06)", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ color: "var(--c-metric-success)", fontWeight: 700, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", mb: 1.5 }}>
                          Invoice
                        </Typography>
                        {match.invoice ? (
                          isEditing ? (
                            <Stack spacing={1.5}>
                              <TextField size="small" label="Vendor" fullWidth
                                value={ef("invVendor")}
                                onChange={(e) => setEf("invVendor", e.target.value)}
                              />
                              <TextField size="small" label="Invoice #" fullWidth
                                value={ef("invNumber")}
                                onChange={(e) => setEf("invNumber", e.target.value)}
                              />
                              <TextField size="small" label="Date" type="date" fullWidth
                                value={ef("invDate")}
                                onChange={(e) => setEf("invDate", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField size="small" label="Amount" fullWidth type="number"
                                value={ef("invAmount")}
                                onChange={(e) => setEf("invAmount", e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              />
                              <Typography variant="body2">
                                <Box component="span" sx={{ color: "text.secondary" }}>Source:</Box>{" "}
                                {match.invoice.source ?? "-"}
                              </Typography>
                            </Stack>
                          ) : (
                            <Stack spacing={0.75}>
                              <Typography variant="body2">
                                <Box component="span" sx={{ color: "text.secondary" }}>Vendor:</Box>{" "}
                                {match.invoice.vendorName ?? "-"}
                              </Typography>
                              <Typography variant="body2">
                                <Box component="span" sx={{ color: "text.secondary" }}>Invoice #:</Box>{" "}
                                {match.invoice.invoiceNumber ?? "-"}
                              </Typography>
                              <Typography variant="body2">
                                <Box component="span" sx={{ color: "text.secondary" }}>Date:</Box>{" "}
                                {match.invoice.date ? formatDate(match.invoice.date) : "-"}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.95rem", mt: 0.5 }}>
                                <Box component="span" sx={{ color: "text.secondary", fontWeight: 400, fontSize: "0.875rem" }}>Amount:</Box>{" "}
                                <Box component="span" sx={hasAmountDiff ? { color: "var(--c-metric-warning)", border: "1px solid rgba(253,203,110,0.3)", borderRadius: 0.5, px: 0.5 } : {}}>
                                  {formatCurrency(match.invoice.amount)}
                                </Box>
                              </Typography>
                              <Typography variant="body2">
                                <Box component="span" sx={{ color: "text.secondary" }}>Source:</Box>{" "}
                                {match.invoice.source ?? "-"}
                              </Typography>
                              {match.invoice.ocrConfidence != null && (
                                <Typography variant="body2">
                                  <Box component="span" sx={{ color: "text.secondary" }}>OCR Confidence:</Box>{" "}
                                  {match.invoice.ocrConfidence}%
                                </Typography>
                              )}
                            </Stack>
                          )
                        ) : (
                          <Typography variant="body2" color="error">Invoice not found</Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Actions row */}
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 2, borderColor: "var(--c-overlay-6)" }} />
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {isEditing ? (
                          <>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={cancelEditing}
                              sx={{ textTransform: "none", fontWeight: 600, borderColor: "var(--c-border-strong)", color: "text.secondary" }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              color="warning"
                              size="small"
                              startIcon={<Save />}
                              disabled={!hasEditChanges(match)}
                              onClick={() => setSaveReasonDialog({ open: true, reason: "" })}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              Save Changes
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => startEditing(match)}
                              sx={{ textTransform: "none", fontWeight: 600, borderColor: "var(--c-border-strong)", color: "text.secondary" }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CompareArrows />}
                              onClick={() => setCompareMatch(match)}
                              sx={{ textTransform: "none", fontWeight: 600, borderColor: "var(--c-border-strong)", color: "text.secondary" }}
                            >
                              View Documents
                            </Button>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleApprove(match._id)}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => setRejectDialog({ open: true, matchId: match._id })}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Side-by-side document comparison */}
      <CompareDrawer
        open={!!compareMatch}
        onClose={() => setCompareMatch(null)}
        match={compareMatch}
      />

      {/* Save edits reason dialog */}
      <Dialog
        open={saveReasonDialog.open}
        onClose={() => setSaveReasonDialog({ open: false, reason: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reason for Edit</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please describe why these changes are necessary. This will be recorded in the audit log for compliance.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason for adjustment"
            placeholder="e.g. OCR extracted wrong amount from invoice PDF, corrected to match Grand Total on page 2..."
            value={saveReasonDialog.reason}
            onChange={(e) => setSaveReasonDialog((prev) => ({ ...prev, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveReasonDialog({ open: false, reason: "" })}>Cancel</Button>
          <Button
            onClick={handleSaveEdits}
            color="warning"
            variant="contained"
            disabled={!saveReasonDialog.reason.trim()}
            startIcon={<Save />}
          >
            Save with Audit Trail
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject confirmation dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, matchId: null })}
      >
        <DialogTitle>Reject Match?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark the match as rejected. The transaction and invoice will be set back to unmatched status. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, matchId: null })}>Cancel</Button>
          <Button onClick={handleRejectConfirm} color="error" variant="contained">Reject</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {howToTarget && (
        <HowToFindModal
          open={Boolean(howToTarget)}
          onClose={() => setHowToTarget(null)}
          txDate={howToTarget.date}
          qb={howToTarget.qb}
        />
      )}
    </Box>
  );
}
